// firebase.ts
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getMessaging, isSupported } from 'firebase/messaging';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const USE_EMULATORS = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

// Local dev runs the emulator suite under a `demo-` project id (see the
// `emulators` script in the root package.json). Demo projects are fully
// offline: the emulators refuse to reach any non-emulated Google service, so
// nothing here can touch ai-sports-1 by accident. The web app must use the
// same id or its Firestore/callable traffic lands in a different namespace
// than the functions emulator's admin SDK writes to.
const EMULATOR_PROJECT_ID = 'demo-athleto';

const firebaseConfig = {
  apiKey: 'AIzaSyBFZKElpF93S-fC3zeU19kQvVhZDLHuUxM',
  authDomain: 'ai-sports-1.firebaseapp.com',
  projectId: 'ai-sports-1',
  storageBucket: 'ai-sports-1.firebasestorage.app',
  messagingSenderId: '969269146771',
  appId: '1:969269146771:web:8aa7827b4aea76e27a8966',
  measurementId: 'G-GT8FS9REZX',
  ...(USE_EMULATORS
    ? {
        projectId: EMULATOR_PROJECT_ID,
        authDomain: `${EMULATOR_PROJECT_ID}.firebaseapp.com`,
        storageBucket: `${EMULATOR_PROJECT_ID}.appspot.com`,
      }
    : {}),
};

export const firebaseApp = initializeApp(firebaseConfig);

// App Check (security audit M1). Attaches an attestation token to every
// Firestore, Storage and callable request, so the backend can ask "is this our
// app?" and not only "who is signed in?". Must be initialized before the
// service handles below, or early requests go out unattested.
//
// Inert without a site key, which keeps local dev and any contributor without
// the key working unchanged. Skipped entirely under the emulators: demo
// projects have no App Check service to attest against.
//
// Enforcement is separate and deliberately not on yet — see
// `server/src/appCheck.ts` for the two-stage rollout. Until stage 2, a missing
// token costs nothing but a console metric.
const APPCHECK_SITE_KEY = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY;
const APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;

if (APPCHECK_SITE_KEY && !USE_EMULATORS) {
  // Lets a developer run against the real project without a reCAPTCHA domain
  // for localhost: register the printed token under App Check > Debug tokens.
  // Must be assigned before initializeAppCheck reads it.
  if (APPCHECK_DEBUG_TOKEN) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string })
      .FIREBASE_APPCHECK_DEBUG_TOKEN = APPCHECK_DEBUG_TOKEN;
  }
  try {
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(APPCHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // A failed init must never take the app down: with enforcement off the
    // requests still succeed, and with it on they fail per-request anyway.
    console.warn('[firebase] App Check initialization failed', error);
  }
}

export const auth        = getAuth(firebaseApp);
export const db          = getFirestore(firebaseApp);
export const storage     = getStorage(firebaseApp);
export const functions   = getFunctions(firebaseApp);

// Ports must match the "emulators" block in firebase.json.
if (USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  console.info(`[firebase] Connected to local emulators (auth/firestore/storage/functions) as ${EMULATOR_PROJECT_ID}`);
}

// Initialize messaging only if supported (not in all browsers/environments)
let messagingInstance: ReturnType<typeof getMessaging> | null = null;

export const getMessagingInstance = async () => {
  if (messagingInstance) return messagingInstance;

  const supported = await isSupported();
  if (supported) {
    messagingInstance = getMessaging(firebaseApp);
    return messagingInstance;
  }
  return null;
};

// Providers
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });
